import React, { useEffect, useState } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import { X, Printer } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Indian-number formatting (1,00,000 style)
const fmtINR = (n) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

// Convert numbers to Indian words  (Rupees X Only)
function numToWords(num) {
  const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const two = (n) => n < 20 ? a[n] : b[Math.floor(n / 10)] + (n % 10 ? ' ' + a[n % 10] : '');
  const three = (n) => {
    const h = Math.floor(n / 100); const r = n % 100;
    return (h ? a[h] + ' Hundred' + (r ? ' ' : '') : '') + (r ? two(r) : '');
  };
  const n = Math.floor(num || 0);
  if (n === 0) return 'Zero';
  let out = '';
  const crore = Math.floor(n / 10000000);
  const lakh = Math.floor((n % 10000000) / 100000);
  const thou = Math.floor((n % 100000) / 1000);
  const hund = n % 1000;
  if (crore) out += two(crore) + ' Crore ';
  if (lakh) out += two(lakh) + ' Lakh ';
  if (thou) out += two(thou) + ' Thousand ';
  if (hund) out += three(hund);
  const paise = Math.round(((num || 0) - n) * 100);
  if (paise) out += ' and ' + two(paise) + ' Paise';
  return out.trim() + ' Only';
}

const formatDate = (d) => {
  if (!d) return '';
  const s = String(d).slice(0, 10);
  if (s.includes('-') && s.split('-')[0].length === 4) {
    const [y, m, dd] = s.split('-');
    return `${dd}-${m}-${y}`;
  }
  return s;
};

const COMPANY_LINES = {
  'Tirth Enterprises': {
    name: 'TIRTH ENTERPRISES',
    phone: '+91 9987951354',
  },
  'Tirth Industries': {
    name: 'TIRTH INDUSTRIES',
    phone: '+91 9987951354',
  },
};

const BANK = { name: 'ICICI Bank Ltd', account: '030105012345', branch: 'Vasai East', ifsc: 'ICIC0000301' };

/**
 * BillPrintPreview — renders the Tirth Enterprises Excel-style invoice on screen.
 * Click "Print" inside to open OS native print dialog (asks for copies, preview, etc).
 * Uses `.ti-print-area` class which the @media print CSS shows exclusively.
 */
export function BillPrintPreview({ orderId, onClose }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  // QR data-URLs — generated locally via the `qrcode` library so they always
  // print, regardless of network access at print time. Hooks must be declared
  // BEFORE any early returns to satisfy React's rules-of-hooks.
  const [upiQRSrc, setUpiQRSrc] = useState('');
  const [traceQRSrc, setTraceQRSrc] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/invoices/${orderId}`, { withCredentials: true });
        setOrder(res.data);
      } catch { toast.error('Failed to load invoice'); onClose(); }
      finally { setLoading(false); }
    })();
  }, [orderId, onClose]);

  // Generate QR images once the order data is loaded.
  useEffect(() => {
    if (!order) return;
    const items = order.items || (order.product_name ? [{
      product_name: order.product_name,
      quantity: order.quantity || 0,
      rate: order.unit_price || 0,
    }] : []);
    const subtotal = items.reduce((s, it) => s + parseFloat(it.quantity || it.qty || 0) * parseFloat(it.rate || it.unit_price || 0), 0);
    const transport = parseFloat(order.transport || 0);
    const extras = Array.isArray(order.extra_charges) ? order.extra_charges.filter(c => parseFloat(c?.amount || 0) > 0) : [];
    const extrasTotal = extras.reduce((s, c) => s + parseFloat(c.amount || 0), 0);
    const total = subtotal + extrasTotal + transport;
    const upiVPA = 'asifkandhal92@okicici';
    const upiPayee = 'Asif Kandhal';
    const upiLink = `upi://pay?pa=${upiVPA}&pn=${encodeURIComponent(upiPayee)}&am=${total}&cu=INR&tn=${encodeURIComponent('Invoice ' + (order.invoice_number || ''))}`;
    const tracePayload = JSON.stringify({
      inv: order.invoice_number || '',
      dt: String(order.invoice_date || order.created_at || '').slice(0, 10),
      client: (order.client || '').slice(0, 40),
      amt: Number(total.toFixed(2)),
      batches: (items || []).flatMap(it => (it.batches_dispatched || []).map(b => b.batch_id).filter(Boolean)).slice(0, 8),
      src: 'Tirth ERP',
    });
    const opts = { errorCorrectionLevel: 'H', margin: 2, width: 320, color: { dark: '#000000', light: '#FFFFFF' } };
    QRCode.toDataURL(upiLink, opts).then(setUpiQRSrc).catch(() => setUpiQRSrc(''));
    QRCode.toDataURL(tracePayload, opts).then(setTraceQRSrc).catch(() => setTraceQRSrc(''));
  }, [order]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center no-print">
        <div className="text-zinc-300 animate-pulse">Loading invoice…</div>
      </div>
    );
  }
  if (!order) return null;

  const isGST = order.gst_enabled !== false && (order.invoice_type === 'GST' || order.gst_enabled === true);
  const entity = isGST ? 'Tirth Industries' : 'Tirth Enterprises';
  const company = COMPANY_LINES[entity];
  const items = order.items || (order.product_name ? [{
    product_name: order.product_name,
    quantity: order.quantity || 0,
    rate: order.unit_price || 0,
    unit: order.unit || 'KG',
  }] : []);

  const subtotal = items.reduce((s, it) => s + (parseFloat(it.quantity || it.qty || 0) * parseFloat(it.rate || it.unit_price || 0)), 0);
  const transport = parseFloat(order.transport_exp || 0);
  // Extra charges entered on the bill: [{label, amount}] — transport/packing/loading/etc.
  // Rendered as additional numbered rows in the items table AND included in the grand total.
  const extraCharges = Array.isArray(order.extra_charges) ? order.extra_charges.filter(c => parseFloat(c?.amount || 0) > 0) : [];
  const extrasTotal = extraCharges.reduce((s, c) => s + parseFloat(c.amount || 0), 0);
  const total = subtotal + extrasTotal + transport;
  const totalQty = items.reduce((s, it) => s + parseFloat(it.quantity || it.qty || 0), 0);
  // Pad blank rows relative to items + extras so the table stays ~10 rows tall
  const emptyRows = Math.max(0, 10 - items.length - extraCharges.length);

  // UPI VPA shown as plain text under the QR on the printed bill. The
  // actual QR data-URL is generated up top in the useEffect.
  const upiVPA = 'asifkandhal92@okicici';
  const upiPayee = 'Asif Kandhal';

  const doPrint = () => {
    // Open a REAL new browser window (breaks out of preview iframe sandbox).
    // SECURITY: avoid `document.write` with templated strings — that's an XSS
    // vector if invoice_number / client / narration ever carries raw HTML.
    // Instead, build the new document via DOM APIs which auto-escape every
    // text node (no string interpolation, no script execution risk).
    const area = document.querySelector('.ti-print-area');
    if (!area) { toast.error('Preview not ready'); return; }
    const win = window.open('', '_blank', 'width=900,height=1100');
    if (!win) { toast.error('Please allow pop-ups to print'); return; }

    const doc = win.document;
    // 1) <title> via createElement → setAttribute / textContent (no parsing)
    const titleEl = doc.createElement('title');
    titleEl.textContent = `Invoice ${order.invoice_number || order.id || ''}`;
    // 2) Inline print stylesheet (safe — static literal, no user input)
    const styleEl = doc.createElement('style');
    styleEl.textContent = `
      @page { margin: 8mm; size: A4; }
      body { margin: 0; font-family: Arial, Helvetica, sans-serif; background: white; color: black; }
      .ti-print-area { width: 100%; background: white; color: black; padding: 0; }
      table { border-collapse: collapse; width: 100%; }
      @media screen { body { padding: 8mm; } }
    `;
    doc.head.appendChild(titleEl);
    doc.head.appendChild(styleEl);
    // 3) Clone the print area instead of serialising it as a string. cloneNode
    // walks the DOM and copies only safe element/attribute nodes — any inline
    // <script> in the source (we don't have any, but defence in depth) is
    // still neutralised because we use a fresh document.
    doc.body.appendChild(doc.importNode(area, true));
    // 4) Trigger print after layout paints. setTimeout dance works on Chrome,
    // Firefox, and Safari. requestAnimationFrame doesn't fire reliably in a
    // freshly-opened window.
    setTimeout(() => { win.focus(); win.print(); }, 200);
    win.onafterprint = () => setTimeout(() => win.close(), 400);
  };

  // Cell style helpers
  // Bumped base font size up from 11px → 13px and padding from 3px → 5px so
  // the invoice fills more of the A4 page without leaving empty space at the
  // bottom. Also bumped header/total/words fonts and made signature rows ~25%
  // taller per user's print-feedback (Feb 2026).
  const td = { border: '1px solid #000', padding: '5px 7px', verticalAlign: 'top', fontSize: '13px', lineHeight: '1.4', color: '#000', fontFamily: 'Arial, Helvetica, sans-serif', wordWrap: 'break-word', overflow: 'hidden' };
  const th = { ...td, fontWeight: 800, textAlign: 'center', background: '#fff', fontSize: '14px' };
  // Subtle zebra-stripe for the line-item rows (1-10). Keeps definition in
  // black-and-white print without overpowering the data.
  const ZEBRA = '#f1f1f1';

  return (
    <div className="fixed inset-0 bg-black/80 z-50 overflow-auto">
      {/* Toolbar (hidden in print) */}
      <div className="no-print sticky top-0 bg-zinc-950/95 backdrop-blur border-b border-zinc-800 px-4 py-3 flex items-center gap-3">
        <h3 className="text-zinc-100 text-sm font-semibold">Invoice Preview · {order.invoice_number || order.id}</h3>
        <span className="text-zinc-500 text-xs">Review, then click Print for OS print dialog (copies, printer select, etc.)</span>
        <div className="flex-1" />
        <button onClick={doPrint} data-testid="print-now-btn"
          className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm px-4 py-2 rounded-lg">
          <Printer size={14} /> Print
        </button>
        <button onClick={onClose} data-testid="close-preview-btn"
          className="p-2 rounded-lg border border-zinc-700 text-zinc-400 hover:bg-zinc-800">
          <X size={14} />
        </button>
      </div>

      {/* ══ A4 Print Area (the ONLY thing shown when @media print active) ══ */}
      <div className="flex justify-center py-6">
        <div
          className="ti-print-area bg-white text-black"
          style={{
            width: '210mm',
            minHeight: '297mm',
            padding: '8mm',
            boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
            fontFamily: 'Arial, Helvetica, sans-serif',
          }}
          data-testid="bill-print-area"
        >
          {/* PROFORMA INVOICE banner — applies to Non-GST (Tirth Enterprises)
              bills; GST bills are tax-invoices and skip this header. */}
          {!isGST && (
            <div style={{
              textAlign: 'center', fontWeight: 800, fontSize: '20px',
              letterSpacing: '2px', padding: '8px 0 4px 0', color: '#000',
              borderBottom: '2px solid #000',
            }}>
              PROFORMA INVOICE
            </div>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '8%' }} />{/* Sr No */}
              <col style={{ width: '42%' }} />{/* Description */}
              <col style={{ width: '12%' }} />{/* Quantity */}
              <col style={{ width: '12%' }} />{/* Rate */}
              <col style={{ width: '6%' }} />{/* per */}
              <col style={{ width: '20%' }} />{/* Amount */}
            </colgroup>
            <tbody>
              {/* ══ HEADER BAND ══ */}
              <tr>
                <td colSpan={2} rowSpan={4} style={{ ...td, fontWeight: 800, fontSize: '22px', padding: '10px 8px', textAlign: 'center', verticalAlign: 'middle', letterSpacing: '0.5px' }}>
                  <div>{company.name}</div>
                  <div style={{ fontWeight: 600, fontSize: '12px', letterSpacing: '0.3px', marginTop: '3px', color: '#000' }}>
                    Mumbai, Maharashtra
                  </div>
                </td>
                <td colSpan={2} style={{ ...td, fontWeight: 700 }}>Bill.No:</td>
                <td colSpan={2} style={{ ...td, fontWeight: 700 }}>Date:</td>
              </tr>
              <tr>
                <td colSpan={2} style={{ ...td, textAlign: 'center', fontWeight: 800, fontSize: '17px' }}>{order.invoice_number || '-'}</td>
                <td colSpan={2} style={{ ...td, textAlign: 'center', fontWeight: 800, fontSize: '17px' }}>{formatDate(order.invoice_date || order.date || order.created_at)}</td>
              </tr>
              <tr>
                <td colSpan={2} style={td}>Delivery Note</td>
                <td colSpan={2} style={td}>Mode/Terms of Payment</td>
              </tr>
              <tr>
                <td colSpan={2} style={td}>{order.lr_no || ''}</td>
                <td colSpan={2} style={td}>{order.payment_terms || ''}</td>
              </tr>
              <tr>
                <td colSpan={2} style={td}>Supplier's Ref.</td>
                <td colSpan={2} style={td}>Other Reference(s)</td>
              </tr>
              <tr>
                <td colSpan={2} style={{ ...td, fontWeight: 700 }}>Buyer</td>
                <td colSpan={2} style={td}>{order.supplier_ref || order.buyer_ref || ''}</td>
                <td colSpan={2} style={td}>{order.other_ref || ''}</td>
              </tr>
              <tr>
                <td colSpan={2} rowSpan={2} style={{ ...td, fontWeight: 800, fontSize: '20px', padding: '8px 8px' }}>
                  {order.client || ''}
                </td>
                <td colSpan={2} style={td}>Order Confirmation</td>
                <td colSpan={2} style={td}>Dated</td>
              </tr>
              <tr>
                <td colSpan={2} style={{ ...td, fontWeight: 700 }}>Verbal</td>
                <td colSpan={2} style={td}>{formatDate(order.due_date || '')}</td>
              </tr>
              <tr>
                <td colSpan={2} rowSpan={2} style={{ ...td, fontSize: '14px', lineHeight: '1.55', padding: '7px 8px', fontWeight: 600 }}>
                  {order.client_address || ''}
                </td>
                <td colSpan={2} style={td}>Despatch No.</td>
                <td colSpan={2} style={td}>Delivery Note Date</td>
              </tr>
              <tr>
                <td colSpan={2} style={td}>{order.dispatch_id || ''}</td>
                <td colSpan={2} style={td}>{formatDate(order.delivery_note_date || '')}</td>
              </tr>
              <tr>
                <td colSpan={2} style={{ ...td, fontSize: '14px', fontWeight: 700 }}>Mob.: +91 {order.client_phone || ''}</td>
                <td colSpan={2} style={td}>Despatched through</td>
                <td colSpan={2} style={td}>Destination</td>
              </tr>
              <tr>
                <td colSpan={2} style={{ ...td, fontSize: '14px', fontWeight: 700 }}>State Name: {order.client_state || 'Maharashtra'}</td>
                <td colSpan={2} style={{ ...td, fontWeight: 700 }}>{order.dispatch_through || ''}</td>
                <td colSpan={2} style={td}>{order.destination || ''}</td>
              </tr>
              <tr>
                <td colSpan={2} style={td}>&nbsp;</td>
                <td colSpan={4} style={td}>Terms of Delivery: {order.terms_of_delivery || ''}</td>
              </tr>

              {/* ══ LINE ITEMS ══ */}
              <tr>
                <th style={th}>Sr. No</th>
                <th style={th}>Description of Goods</th>
                <th style={th}>Quantity</th>
                <th style={th}>Rate</th>
                <th style={th}>per</th>
                <th style={th}>Amount</th>
              </tr>
              {items.map((it, i) => {
                const qty = parseFloat(it.quantity || it.qty || 0);
                const rate = parseFloat(it.rate || it.unit_price || 0);
                const amt = qty * rate;
                const stripe = i % 2 === 1 ? { background: ZEBRA } : {};
                return (
                  <tr key={i}>
                    <td style={{ ...td, ...stripe, textAlign: 'center', fontWeight: 700, fontSize: '14px' }}>{i + 1}</td>
                    <td style={{ ...td, ...stripe, fontSize: '13px', fontWeight: 600 }}>
                      {it.product_code && <span style={{ fontFamily: 'monospace', color: '#333', fontWeight: 700, marginRight: '6px' }}>[{it.product_code}]</span>}
                      {it.product_name || it.name || ''}
                    </td>
                    <td style={{ ...td, ...stripe, textAlign: 'right', fontSize: '14px', fontWeight: 700 }}>{qty ? qty : ''}</td>
                    <td style={{ ...td, ...stripe, textAlign: 'right', fontSize: '14px', fontWeight: 700 }}>{rate ? rate.toFixed(2) : ''}</td>
                    <td style={{ ...td, ...stripe, textAlign: 'center', fontSize: '12px' }}>{it.unit || 'KG'}</td>
                    <td style={{ ...td, ...stripe, textAlign: 'right', whiteSpace: 'nowrap', fontSize: '14px', fontWeight: 700 }}>{amt ? fmtINR(amt) : '0'}</td>
                  </tr>
                );
              })}
              {/* Extra charges — Transport / Packing / Loading etc. rendered as own numbered rows */}
              {extraCharges.map((c, i) => {
                const stripe = (items.length + i) % 2 === 1 ? { background: ZEBRA } : {};
                return (
                <tr key={`xc-${i}`} data-testid={`extra-charge-row-${i}`}>
                  <td style={{ ...td, ...stripe, textAlign: 'center', fontWeight: 700, fontSize: '14px' }}>{items.length + i + 1}</td>
                  <td style={{ ...td, ...stripe, fontStyle: 'italic', fontSize: '13px', fontWeight: 600 }}>{(c.label || 'Charge').toString()}</td>
                  <td style={{ ...td, ...stripe }}>&nbsp;</td>
                  <td style={{ ...td, ...stripe }}>&nbsp;</td>
                  <td style={{ ...td, ...stripe }}>&nbsp;</td>
                  <td style={{ ...td, ...stripe, textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 700, fontSize: '14px' }}>{fmtINR(parseFloat(c.amount || 0))}</td>
                </tr>
                );
              })}
              {Array.from({ length: emptyRows }).map((_, i) => {
                const idx = items.length + extraCharges.length + i;
                const stripe = idx % 2 === 1 ? { background: ZEBRA } : {};
                return (
                <tr key={`e-${i}`}>
                  <td style={{ ...td, ...stripe, textAlign: 'center' }}>{idx + 1}</td>
                  <td style={{ ...td, ...stripe }}>&nbsp;</td>
                  <td style={{ ...td, ...stripe }}>&nbsp;</td>
                  <td style={{ ...td, ...stripe }}>&nbsp;</td>
                  <td style={{ ...td, ...stripe }}>&nbsp;</td>
                  <td style={{ ...td, ...stripe, textAlign: 'right' }}>0</td>
                </tr>
                );
              })}
              <tr>
                <td style={td}>&nbsp;</td>
                <td style={{ ...td, fontWeight: 800, textAlign: 'right', fontSize: '16px' }}>TOTAL</td>
                <td style={{ ...td, fontWeight: 800, textAlign: 'right', fontSize: '16px' }}>{totalQty ? totalQty : 0}</td>
                <td style={td}>&nbsp;</td>
                <td style={td}>&nbsp;</td>
                <td style={{ ...td, fontWeight: 800, textAlign: 'right', fontSize: '16px', whiteSpace: 'nowrap' }}>{fmtINR(total)}</td>
              </tr>

              {/* ══ AMOUNT IN WORDS + E.&O.E ══ */}
              <tr>
                <td colSpan={5} style={{ ...td, fontWeight: 700, fontSize: '13px' }}>Amount Chargeable (in words)</td>
                <td colSpan={1} style={{ ...td, textAlign: 'right', fontStyle: 'italic' }}>E. & O.E</td>
              </tr>
              <tr>
                <td colSpan={6} style={{ ...td, fontWeight: 800, fontSize: '15px', padding: '8px' }}>
                  Rupees {numToWords(total)}
                </td>
              </tr>

              {/* ══ FOOTER: Declaration | Payment Details (UPI + small QR) ══ */}
              <tr>
                <td colSpan={2} rowSpan={2} style={{ ...td, fontSize: '10.5px', lineHeight: '1.55', verticalAlign: 'top', padding: '6px 8px' }}>
                  <div style={{ fontWeight: 700, marginBottom: '6px' }}>Declaration</div>
                  <div>We hereby certify that this invoice shows the actual price of the goods described and that all particulars are true and correct.</div>
                  <div style={{ marginTop: '8px' }}>Please check the goods at the time of delivery; the company is not responsible for shortages or breakages thereafter.</div>
                  <div style={{ marginTop: '14px', fontWeight: 700 }}>Thank you for your business!</div>
                </td>
                <td colSpan={4} style={{ ...td, fontWeight: 700, fontSize: '11.5px', padding: '5px 8px' }}>Payment Details</td>
              </tr>
              <tr>
                <td colSpan={4} style={{ ...td, padding: '8px 10px', verticalAlign: 'middle' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                    <div>
                      <div style={{ fontSize: '15px', fontWeight: 800 }}>
                        UPI ID:&nbsp;<span style={{ fontFamily: 'Courier New, monospace' }}>{upiVPA}</span>
                      </div>
                      <div style={{ fontSize: '13px', marginTop: '4px', fontWeight: 700 }}>
                        UPI No:&nbsp;<span style={{ fontFamily: 'Courier New, monospace', fontWeight: 800 }}>+91 98339 80606</span>
                      </div>
                      <div style={{ fontSize: '13px', marginTop: '4px', fontWeight: 700 }}>
                        Name:&nbsp;<span style={{ fontWeight: 700 }}>{upiPayee}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ background: '#fff', padding: '2px', border: '2px solid #000', display: 'inline-block' }}>
                        <img src={upiQRSrc} alt="Scan to Pay" style={{ width: '82px', height: '82px', display: 'block' }} />
                      </div>
                      <div style={{ fontSize: '10px', fontStyle: 'italic', marginTop: '3px', fontWeight: 600 }}>Scan to Pay</div>
                    </div>
                  </div>
                </td>
              </tr>

              {/* ══ SIGNATURE BAND: Receiver (L) | Traceability QR + Authorized Signatory (R) ══
                  Row height bumped from 96px → 120px (~25% taller) per user feedback so
                  there's actual blank space for a stamp + signature. */}
              <tr>
                <td colSpan={2} style={{ ...td, verticalAlign: 'bottom', padding: '6px 8px', height: '120px' }}>
                  <div style={{ fontWeight: 800, fontSize: '13px' }}>Receiver's Sign / Receiver's Stamp</div>
                </td>
                <td colSpan={4} style={{ ...td, padding: '6px 8px', height: '120px', verticalAlign: 'top' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', height: '100%' }}>
                    {/* QR codes get a 2px black border + white background pad so they
                        survive black-and-white printing — without this they often
                        wash out on grayscale lasers. */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ background: '#fff', padding: '2px', border: '2px solid #000', display: 'inline-block' }}>
                        <img src={traceQRSrc} alt="Traceability" style={{ width: '76px', height: '76px', display: 'block' }} />
                      </div>
                      <div style={{ fontSize: '10px', fontStyle: 'italic', marginTop: '3px', fontWeight: 600 }}>Scan for Traceability</div>
                    </div>
                    <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignSelf: 'stretch' }}>
                      <div style={{ fontWeight: 800, fontSize: '13px' }}>Authorized Signatory</div>
                      <div style={{ fontWeight: 800, fontSize: '13px', marginTop: '2px' }}>{company.name}</div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
